import { useMemo, useState } from 'preact/hooks';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import { formatChannels } from '../../lib/channels.js';
import { PrinterEditModal } from './PrinterEditModal.jsx';

const TEAL = '#5DD0C8';

const STATUS_META = {
  active:         { label: 'Active',         variant: 'ok'    },
  semi_active:    { label: 'Semi-active',    variant: 'warn'  },
  inactive:       { label: 'Inactive',       variant: 'muted' },
  decommissioned: { label: 'Decommissioned', variant: 'muted' },
};

const STATUS_PILL = {
  ok:    'border-status-ok/[0.3] bg-status-ok/[0.12] text-status-ok',
  warn:  'border-ink-yellow/[0.3] bg-ink-yellow/[0.12] text-ink-yellow',
  muted: 'border-border-strong text-text-tertiary',
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] ?? STATUS_META.active;
  return (
    <span class={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_PILL[meta.variant]}`}>
      <span class="block h-1.5 w-1.5 rounded-full bg-current" />
      {meta.label}
    </span>
  );
};

const PrinterCard = ({ printer }) => {
  const channelsLabel = printer.kind === 'proofer' && printer.channels.length
    ? formatChannels(printer.channels)
    : null;
  const isProofer = printer.kind === 'proofer';
  // Office printers structurally never have mix recipes, so that line is
  // always true — proofers have no cheap per-card issue/stock count without
  // an N+1 query, so nothing renders there instead of a fabricated "0".
  const hasBottomBar = !isProofer || printer.webManagerUrl;
  return (
    <a
      href={`/printers/${printer.id}`}
      class="block rounded-lg border border-border-subtle bg-bg-card p-4 transition hover:border-border-strong hover:bg-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      <div class="mb-2.5 flex items-center justify-between">
        <StatusPill status={printer.status} />
        <span class="text-[10px] font-semibold uppercase tracking-eyebrow" style={{ color: TEAL }}>
          {isProofer ? 'Proofer' : 'Office'}
        </span>
      </div>
      <p class="m-0 mb-1 font-serif text-[18px] font-medium tracking-tight text-text-primary">{printer.name}</p>
      <p class="m-0 mb-3 text-[12px] font-medium text-text-tertiary">
        {printer.ipAddress ? (
          <span class="font-mono text-text-body">{printer.ipAddress}</span>
        ) : (
          <span class="text-text-muted">No IP</span>
        )}
        {channelsLabel && <span class="ml-2">· {channelsLabel}</span>}
        {!isProofer && printer.location && <span class="ml-2">· {printer.location}</span>}
      </p>
      {hasBottomBar && (
        <div class="flex items-center gap-3 border-t border-border-subtle pt-2.5 text-[11px] font-medium text-text-muted">
          {!isProofer && <span>No mix recipes</span>}
          {printer.webManagerUrl && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); window.open(printer.webManagerUrl, '_blank', 'noopener,noreferrer'); }}
              class="ml-auto font-semibold"
              style={{ color: TEAL }}
            >
              Web manager ↗
            </button>
          )}
        </div>
      )}
    </a>
  );
};

const Stat = ({ label, value, accent = TEAL }) => (
  <div class="relative overflow-hidden rounded-sm border border-border-subtle bg-bg-card p-3">
    <span class="absolute inset-y-0 left-0 w-[3px]" style={{ background: accent }} aria-hidden="true" />
    <p class="m-0 text-[11px] font-semibold uppercase tracking-eyebrow text-text-tertiary">{label}</p>
    <p class="m-0 mt-1 font-serif text-[26px] font-medium tracking-tighter text-text-primary">{value}</p>
  </div>
);

export const PrintersIndexPage = () => {
  const { data: printers, loading, error, refetch } = useApi('/printers?includeInactive=true');
  const { data: stats } = useApi('/printers/stats');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [kindFilter, setKindFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);

  const counts = useMemo(() => {
    if (!printers) return { all: 0, proofer: 0, office: 0 };
    return printers.reduce(
      (acc, p) => {
        acc.all += 1;
        acc[p.kind] = (acc[p.kind] ?? 0) + 1;
        return acc;
      },
      { all: 0, proofer: 0, office: 0 },
    );
  }, [printers]);

  const filtered = useMemo(() => {
    if (!printers) return [];
    return kindFilter === 'all' ? printers : printers.filter((p) => p.kind === kindFilter);
  }, [printers, kindFilter]);

  return (
    <div>
      <section class="mb-6 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: TEAL }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: TEAL }}>
              Printers
            </span>
          </div>
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">
            Network printers
          </h1>
          <p class="m-0 text-sm text-text-secondary">
            Proofers and office printers — IP, status, supplies, and quick access to web managers.
          </p>
        </div>
        {isAdmin && (
          <Button variant="secondary" onClick={() => setModalOpen(true)}>+ Add printer</Button>
        )}
      </section>

      {stats && (
        <div class="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Total" value={stats.total} />
          <Stat label="Proofers" value={stats.byKind.proofer ?? 0} />
          <Stat label="Semi-active" value={stats.byStatus.semi_active ?? 0} accent="#F4C430" />
          <Stat label="Issues open" value={stats.openIssues ?? '—'} accent="#E63B7A" />
        </div>
      )}

      <div class="mb-4 flex items-center justify-between gap-3">
        <div class="flex overflow-hidden rounded-md border border-border-strong">
          {[
            { v: 'all',     label: `All · ${counts.all}` },
            { v: 'proofer', label: `Proofers · ${counts.proofer}` },
            { v: 'office',  label: `Office · ${counts.office}` },
          ].map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => setKindFilter(o.v)}
              class={`px-3 py-1.5 text-[12px] font-semibold ${kindFilter === o.v ? 'text-[#0E2622]' : 'text-text-tertiary hover:bg-bg-card'}`}
              style={kindFilter === o.v ? { background: TEAL } : undefined}
            >
              {o.label}
            </button>
          ))}
        </div>
        <span class="text-[12px] font-medium text-text-muted">Sorted by status</span>
      </div>

      {loading && <p class="text-sm text-text-tertiary">Loading…</p>}
      {error && (
        <p class="text-sm font-semibold text-ink-magenta">Failed to load: {error.message}</p>
      )}

      {filtered.length > 0 && (
        <div class="grid grid-cols-1 gap-2.5 md:grid-cols-2">
          {filtered.map((p) => <PrinterCard key={p.id} printer={p} />)}
        </div>
      )}
      {printers && filtered.length === 0 && (
        <div class="surface-card p-6 text-center">
          <p class="m-0 font-serif text-lg font-medium text-text-primary">No printers yet.</p>
          <p class="m-0 mt-1 text-sm text-text-secondary">
            {isAdmin ? 'Click "+ Add printer" above to record one.' : 'An admin can add printers from this page.'}
          </p>
        </div>
      )}

      <PrinterEditModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={() => { setModalOpen(false); refetch(); }}
      />
    </div>
  );
};
